/* THE COUNTER — behaviour for sections/counter-shelf (collection + search).
   - Filters, sort, chips and pills re-render the section through the Section Rendering API and
     push the real URL, so every state is addressable and the back button works (popstate).
   - Phone: the rail is a bottom sheet; it stays open across re-renders so the Apply button can
     name the new count, and closes on Apply / scrim / close.
   - Pagination auto-loads at the foot of the grid, at most THREE times, then leaves the
     Load more button (brief: "handover after three loads"). The URL follows the last page loaded.
   - Cards: shade/size selection and Add to bag, same rules as the rails (counter-recs.js).
     Added to bag holds 2.4s in Plum, then returns (brief, card modifiers). The drawer opens.
     Kit "Choose options" still goes to the PDP here: the in-cart panel is not wired to the grid yet. */
(() => {
  if (window.__counterShelf) return;
  window.__counterShelf = true;
  const root = () => document.querySelector('[data-cs-root]');
  const cartRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  let sheetOpen = false;
  let loads = 0;
  let io = null;
  let busy = false;

  const sectionUrl = url => {
    const u = new URL(url, location.origin);
    u.searchParams.set('section_id', root().dataset.section);
    return u.toString();
  };
  const fetchDoc = async url => {
    const r = await fetch(sectionUrl(url), { credentials: 'same-origin' });
    if (!r.ok) throw new Error('shelf ' + r.status);
    return new DOMParser().parseFromString(await r.text(), 'text/html');
  };

  const render = async (url, push = true) => {
    const el = root();
    if (!el || busy) return;
    busy = true;
    const main = el.querySelector('.cs-main');
    if (main) main.setAttribute('aria-busy', 'true');
    try {
      const doc = await fetchDoc(url);
      const next = doc.querySelector('[data-cs-root]');
      if (!next) throw new Error('no root');
      el.innerHTML = next.innerHTML;
      const clean = new URL(url, location.origin);
      clean.searchParams.delete('section_id');
      if (push) history.pushState({ cs: 1 }, '', clean.pathname + clean.search);
      loads = 0;
      if (sheetOpen) setSheet(true);
      wireMore();
    } catch (e) {
      location.href = url;
    } finally {
      busy = false;
    }
  };

  const formUrl = form => {
    const u = new URL(form.getAttribute('action'), location.origin);
    const fd = new FormData(form);
    for (const [k, v] of fd.entries()) {
      if (!v) continue;
      if (k.startsWith('filter.') || k === 'sort_by' || k === 'q' || k === 'type') u.searchParams.append(k, v);
    }
    return u.pathname + u.search;
  };

  const setSheet = open => {
    const el = root();
    if (!el) return;
    const rail = el.querySelector('[data-cs-rail]');
    const scrim = el.querySelector('.cs-scrim');
    const btn = el.querySelector('[data-cs-open]');
    sheetOpen = open;
    if (rail) open ? rail.setAttribute('data-open', '') : rail.removeAttribute('data-open');
    if (scrim) scrim.hidden = !open;
    if (btn) btn.setAttribute('aria-expanded', String(open));
    document.documentElement.style.overflow = open ? 'hidden' : '';
  };

  // ---- Filters, sort, links ----
  let t;
  document.addEventListener('change', e => {
    const el = root();
    if (!el || !el.contains(e.target)) return;
    const form = e.target.closest('[data-cs-form]');
    if (!form) return;
    if (e.target.matches('[data-cs-filter], [data-cs-sort]')) render(formUrl(form));
    else if (e.target.matches('.cs-price input')) { clearTimeout(t); t = setTimeout(() => render(formUrl(form)), 500); }
  });
  document.addEventListener('submit', e => {
    const form = e.target.closest('[data-cs-form]');
    if (!form || !root() || !root().contains(form)) return;
    e.preventDefault();
    render(formUrl(form));
  });
  document.addEventListener('click', e => {
    const el = root();
    if (!el || !el.contains(e.target)) return;
    const nav = e.target.closest('[data-cs-nav]');
    if (nav) { e.preventDefault(); render(nav.getAttribute('href')); return; }
    if (e.target.closest('[data-cs-open]')) { setSheet(true); return; }
    if (e.target.closest('[data-cs-close]')) { setSheet(false); return; }
    const more = e.target.closest('[data-cs-more-btn]');
    if (more) { e.preventDefault(); loadMore(true); }
  });
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && sheetOpen) setSheet(false); });
  window.addEventListener('popstate', () => { if (root()) render(location.href, false); });

  // ---- Pagination ----
  const loadMore = async manual => {
    const el = root();
    const box = el && el.querySelector('[data-cs-more]');
    if (!box || box.dataset.loading) return;
    box.dataset.loading = '1';
    try {
      const doc = await fetchDoc(box.dataset.next);
      const grid = el.querySelector('[data-cs-grid]');
      const incoming = doc.querySelector('[data-cs-grid]');
      if (grid && incoming) incoming.querySelectorAll(':scope > [data-crc]').forEach(c => grid.appendChild(c));
      const nextBox = doc.querySelector('[data-cs-more]');
      const clean = new URL(box.dataset.next, location.origin);
      clean.searchParams.delete('section_id');
      history.replaceState({ cs: 1 }, '', clean.pathname + clean.search);
      if (!manual) loads++;
      if (nextBox) { box.replaceWith(nextBox); wireMore(); } else box.remove();
    } catch (err) {
      delete box.dataset.loading;
    }
  };
  const wireMore = () => {
    if (io) io.disconnect();
    const box = root() && root().querySelector('[data-cs-more]');
    if (!box) return;
    const btn = box.querySelector('[data-cs-more-btn]');
    if (loads >= 3 || !('IntersectionObserver' in window)) { if (btn) btn.hidden = false; return; }
    if (btn) btn.hidden = true;
    io = new IntersectionObserver(entries => { if (entries.some(en => en.isIntersecting)) loadMore(false); }, { rootMargin: '600px 0px' });
    io.observe(box);
  };

  // ---- Cards ----
  const openDrawer = () => {
    const d = document.getElementById('cart-drawer');
    if (d && typeof d.show === 'function') { try { d.show(); return; } catch (e) {} }
    const opener = document.querySelector('[aria-controls="cart-drawer"]');
    if (opener) opener.click();
  };
  document.addEventListener('change', e => {
    const radio = e.target.closest('[data-cs-root] .crc__radio');
    if (!radio) return;
    const card = radio.closest('[data-crc]');
    let vs = [];
    try { vs = JSON.parse(card.querySelector('[data-crc-variants]').textContent); } catch (err) {}
    const v = vs.find(x => x.id === +radio.value);
    if (!v) return;
    card.dataset.variant = v.id;
    const price = card.querySelector('[data-crc-price]');
    if (price) price.innerHTML = v.c ? `<s class="crc__was">${esc(v.c)}</s> <span class="crc__now">${esc(v.p)}</span>` : esc(v.p);
    const label = card.querySelector('[data-crc-label]');
    if (label && card.dataset.case === 'shade') label.textContent = v.o;
    const btn = card.querySelector('[data-crc-add]');
    if (btn) { btn.disabled = !v.a; btn.textContent = v.a ? 'Add to bag' : 'Sold out'; }
  });
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-cs-root] [data-crc-add]');
    if (!btn || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    e.preventDefault();
    const card = btn.closest('[data-crc]');
    const id = +card.dataset.variant;
    if (!id) return;
    const label = btn.textContent;
    btn.setAttribute('aria-busy', 'true'); btn.disabled = true; btn.textContent = 'Adding…';
    try {
      const r = await fetch(`${cartRoot}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id, quantity: 1 }] }) });
      if (!r.ok) throw new Error('add ' + r.status);
      btn.textContent = 'Added to bag';
      btn.style.background = '#A35F6E'; btn.style.borderColor = '#A35F6E'; btn.style.color = '#fff';
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      openDrawer();
      setTimeout(() => { btn.textContent = label; btn.style.background = ''; btn.style.borderColor = ''; btn.style.color = ''; btn.disabled = false; btn.removeAttribute('aria-busy'); }, 2400);
    } catch (err) {
      btn.removeAttribute('aria-busy'); btn.disabled = false; btn.textContent = 'Try again';
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wireMore); else wireMore();
})();
